import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import '../styles/TeamDecision.css';

const TeamDecision = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isReturningUser, setIsReturningUser] = useState(false);
  const inviteParam = searchParams.get('invite') || searchParams.get('code') || '';
  const currentSeasonYear = String(new Date().getFullYear());

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (!u) {
        setError('You need to be signed in to choose a team path.');
        setLoading(false);
        return;
      }
      
      // Check if user has a teamId (returning user)
      try {
        const userRef = doc(db, `users/${u.uid}`);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          setIsReturningUser(!!userData.teamId);
        }
      } catch (err) {
        console.error('Failed to check user data', err);
      } finally {
        setLoading(false);
      }
    });
    return unsub;
  }, []);

  const effectiveInvite = inviteParam || '';
  const hasInvite = !!effectiveInvite;

  const heroName = user?.displayName || 'Player';
  const heroAvatar = user?.photoURL;

  return (
    <div className="team-decision-container">
      <button className="back-to-home" onClick={() => navigate('/landing')}>
        ← Back to Home
      </button>
      <div className="team-stack">
        <div className="hero">
          <div className="hero-left">
            <div className="avatar" aria-hidden="true">
              {heroAvatar ? <img src={heroAvatar} alt="avatar" /> : <span>{heroName[0]}</span>}
            </div>
            <div>
              {isReturningUser && <p className="eyebrow">Welcome back</p>}
              <h1 className="headline">Choose how to start, {heroName}</h1>
              <p className="subhead">Starting a new season or joining an existing squad?</p>
              {error && (
                <p className="error-banner" role="alert" aria-live="assertive">
                  {error}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="team-options-wrapper">
          <div
            className="team-option glass-left featured"
            tabIndex={0}
            onClick={() => navigate('/create-team')}
            onKeyDown={(e) => e.key === 'Enter' && navigate('/create-team')}
          >
            <div className="pill">Team captains only — Discord approval required</div>
            <h2>CREATE A TEAM</h2>
            <p className="team-description">Set up your squad, generate an invite, and kick off the {currentSeasonYear} season.</p>
          </div>

          <div
            className="team-option glass-right"
            tabIndex={0}
            onClick={() =>
              navigate(`/join-team${effectiveInvite ? `?invite=${effectiveInvite}` : ''}`, {
                state: {
                  source: 'team-decision',
                  from: `${location.pathname}${location.search}`,
                },
              })
            }
            onKeyDown={(e) =>
              e.key === 'Enter' &&
              navigate(`/join-team${effectiveInvite ? `?invite=${effectiveInvite}` : ''}`, {
                state: {
                  source: 'team-decision',
                  from: `${location.pathname}${location.search}`,
                },
              })
            }
          >
            <h2>JOIN A TEAM</h2>
            <p className="team-description">No Discord needed. Browse teams and send a request directly to the captain.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamDecision;
